/**
 * AssistantActionCard — the shared shell for assistant chat messages that carry an
 * inline action (a CTA button, a selectable option set, or an intake surface).
 *
 * Extracted from the questionnaire builder's DecisionCard / CollectCard so every
 * "message with a button" across the app — questionnaire decisions, asset intake, and
 * the Quick Study pre-run approval — shares ONE bubble anatomy: the soft
 * `study-chat-ai-bubble` surface with the tail notch (`rounded-tl-sm`), an optional AI
 * avatar column, and an optional `below` slot for content that must sit outside the
 * bubble (e.g. locked decision options).
 *
 * Presentational only: no state, no network. Callers own what goes in the bubble and
 * the footer.
 */
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

export interface AssistantActionCardProps {
  /**
   * AI avatar image src. When provided, the avatar column renders to the left of the
   * bubble; when omitted, no avatar (and no reserved space) is shown — used where the
   * surrounding thread doesn't carry per-message avatars (e.g. Quick Study).
   */
  aiIconSrc?: string
  /** data-testid applied to the bubble element (preserves existing card testids). */
  bubbleTestId?: string
  /**
   * Overrides the bubble's surface classes (background / text / border). Defaults to the
   * soft `study-chat-ai-bubble` used by the questionnaire cards. Pass a neutral outlined
   * surface where the default green would clash with the surrounding thread (e.g. Quick
   * Study, whose user messages are already green).
   */
  bubbleClassName?: string
  /**
   * `bubble` — chat turn with optional avatar + tail notch (default).
   * `composer` — full-width input shell matching ChatComposer (pinned in the QS composer slot).
   * `plain` — no avatar, no bubble chrome: children render as a full-width left-aligned block,
   *   matching modern LLM chat surfaces (ChatGPT / Claude) where the assistant speaks as bare prose.
   */
  surface?: 'bubble' | 'composer' | 'plain'
  /** Dims the bubble (locked / answered / superseded states). */
  dimmed?: boolean
  /** The bubble body. */
  children: React.ReactNode
  /** Optional content rendered as a sibling UNDER the bubble (still inside the column). */
  below?: React.ReactNode
}

export function AssistantActionCard({
  aiIconSrc,
  bubbleTestId,
  bubbleClassName,
  surface = 'bubble',
  dimmed,
  children,
  below,
}: AssistantActionCardProps) {
  const { t } = useTranslation('app')
  const bubbleClasses =
    surface === 'composer'
      ? cn(
          'rounded-2xl bg-white px-5 py-4 text-base leading-[1.6] text-twin-chat-fg shadow-[0px_15px_30px_0px_rgba(0,0,0,0.2)] dark:bg-twin-chat-surface',
          bubbleClassName,
          dimmed && 'opacity-70',
        )
      : cn(
          'rounded-2xl rounded-tl-sm px-4 pt-3 pb-3 text-base leading-[1.6]',
          bubbleClassName ?? 'bg-study-chat-ai-bubble text-foreground',
          dimmed && 'opacity-70',
        )

  if (surface === 'composer') {
    return (
      <div className="min-w-0 flex-1 text-left">
        <div data-testid={bubbleTestId} className={bubbleClasses}>
          {children}
        </div>
        {below}
      </div>
    )
  }

  // Plain assistant turn (ChatGPT / Claude style): no avatar column, no bubble background,
  // padding or tail notch — just the children as a full-width, left-aligned prose block.
  if (surface === 'plain') {
    return (
      <div className="min-w-0 flex-1 text-left">
        <div
          data-testid={bubbleTestId}
          className={cn('text-base leading-[1.6] text-twin-chat-fg', bubbleClassName, dimmed && 'opacity-70')}
        >
          {children}
        </div>
        {below}
      </div>
    )
  }

  return (
    // The `pr-[60px]` right gutter is a chat-bubble affordance — it keeps avatar'd turns from
    // running to the thread's edge and balances the left avatar column. In the avatar-less panel
    // context (e.g. the Quick Study composer slot) there's no avatar to balance and the card
    // should span the full panel width, matching the ChatComposer it replaces — so drop it.
    <div className={cn('flex flex-row gap-2', aiIconSrc !== undefined && 'pr-[60px]')}>
      {aiIconSrc !== undefined && (
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-study-chat-ai-bubble">
          <img
            src={aiIconSrc}
            alt={t('chatChrome.aiFallback')}
            className="h-full w-full object-contain"
            style={{ transform: 'scale(1.3)' }}
          />
        </div>
      )}

      <div className="min-w-0 flex-1 text-left">
        <div
          data-testid={bubbleTestId}
          className={bubbleClasses}
        >
          {children}
        </div>
        {below}
      </div>
    </div>
  )
}
